import type {
    CustomSpawnBracketRecord,
    HordeCommitDocument,
    HordeGameEventDocument,
    HordeGameIntentDocument,
    HordePlayerSecretViewDocument,
} from '@animus/type/domain';
import { HordeAuthorityDocumentSchema, PersistedHordeGameSchema } from '@animus/type/domain';
import type { CustomSpawnSimulatorSessionRecord } from '@animus/utility/web/custom-spawn-persistence';
import type { RxJsonSchema } from 'rxdb';
import { z } from 'zod';

/**
 * Game-bearing collections use the same closed JSON contracts as the persistence boundary. This prevents a direct
 * collection write from retaining a copied datasheet/card/rules object that the domain parser would reject.
 */
export const STABLE_ENVELOPE_SCHEMA_VERSION = 2;

export const customSpawnBracketsSchema = stableEnvelopeSchema<CustomSpawnBracketRecord>({
    maxPrimaryKeyLength: 64,
    primaryKey: 'id',
    properties: {
        updatedAt: { type: 'string' },
    },
    required: ['updatedAt'],
});

export const customSpawnSimulatorSessionsSchema = stableEnvelopeSchema<CustomSpawnSimulatorSessionRecord>({
    indexes: [['bracketId', 'updatedAt']],
    maxPrimaryKeyLength: 128,
    primaryKey: 'sessionId',
    properties: {
        bracketId: { maxLength: 128, minLength: 1, type: 'string' },
        state: { type: 'object' },
        updatedAt: { type: 'string' },
    },
    required: ['bracketId', 'state', 'updatedAt'],
});

export const hordeAuthoritiesSchema = domainDocumentSchema(HordeAuthorityDocumentSchema, 'gameId');

/**
 * Bumped past `STABLE_ENVELOPE_SCHEMA_VERSION` for the two collections `commitAuthorityTransition` scans on every
 * commit (`hordeCommits`, `hordeGameEvents`). Adding a compound index requires a schema version bump under RxDB even
 * though no document shape changed, so `rxdb-factory.ts` carries an identity migration for these two collections
 * from `STABLE_ENVELOPE_SCHEMA_VERSION` to this version. Every other collection stays on the unbumped constant.
 */
export const INDEXED_JOURNAL_SCHEMA_VERSION = STABLE_ENVELOPE_SCHEMA_VERSION + 1;

/**
 * Bumped past `STABLE_ENVELOPE_SCHEMA_VERSION` for `hordeGameIntents`, which gained the two compound indexes the
 * host outbox and joiner-result live queries run on every change (`gameId`+`status`, `gameId`+`actorDeviceId`).
 * Same identity-migration requirement as the journal collections - no document shape changed, `rxdb-factory.ts`
 * carries the identity strategy for the bump.
 */
export const INDEXED_INTENTS_SCHEMA_VERSION = STABLE_ENVELOPE_SCHEMA_VERSION + 1;

export const hordeCommitsSchema = stableEnvelopeSchema<HordeCommitDocument>({
    indexes: [
        ['gameId', 'status'],
        ['gameId', 'updatedAt'],
    ],
    maxPrimaryKeyLength: 128,
    primaryKey: 'commitId',
    properties: {
        gameId: { maxLength: 128, minLength: 1, type: 'string' },
        status: { enum: ['prepared', 'projectionPending', 'projected'], maxLength: 32, type: 'string' },
        updatedAt: { maxLength: 64, type: 'string' },
    },
    required: ['gameId', 'status', 'updatedAt'],
    version: INDEXED_JOURNAL_SCHEMA_VERSION,
});

export const hordeGameEventsSchema = stableEnvelopeSchema<HordeGameEventDocument>({
    indexes: [['gameId', 'revision']],
    maxPrimaryKeyLength: 128,
    primaryKey: 'eventId',
    properties: {
        gameId: { maxLength: 128, minLength: 1, type: 'string' },
        privatePlayerId: {
            anyOf: [{ maxLength: 128, minLength: 1, type: 'string' }, { type: 'null' }],
        },
        revision: { maximum: Number.MAX_SAFE_INTEGER, minimum: 1, multipleOf: 1, type: 'integer' },
        visibility: { enum: ['public', 'privatePlayer'], type: 'string' },
    },
    required: ['gameId', 'privatePlayerId', 'revision', 'visibility'],
    version: INDEXED_JOURNAL_SCHEMA_VERSION,
});

export const hordeGameIntentsSchema = stableEnvelopeSchema<HordeGameIntentDocument>({
    indexes: [
        ['gameId', 'status'],
        ['gameId', 'actorDeviceId'],
    ],
    maxPrimaryKeyLength: 128,
    primaryKey: 'intentId',
    properties: {
        actorDeviceId: { maxLength: 128, minLength: 1, type: 'string' },
        gameId: { maxLength: 128, minLength: 1, type: 'string' },
        outboxScopeId: { maxLength: 128, minLength: 1, type: 'string' },
        status: { enum: ['pending', 'accepted', 'rejected'], maxLength: 32, type: 'string' },
        updatedAt: { type: 'string' },
    },
    required: ['actorDeviceId', 'gameId', 'outboxScopeId', 'status', 'updatedAt'],
    version: INDEXED_INTENTS_SCHEMA_VERSION,
});

export const hordePlayerSecretViewsSchema = stableEnvelopeSchema<HordePlayerSecretViewDocument>({
    maxPrimaryKeyLength: 128,
    primaryKey: 'documentId',
    properties: {
        gameId: { maxLength: 128, minLength: 1, type: 'string' },
        playerId: { maxLength: 128, minLength: 1, type: 'string' },
    },
    required: ['gameId', 'playerId'],
});

export const hordeSessionsSchema = domainDocumentSchema(PersistedHordeGameSchema, 'gameId');

function domainDocumentSchema<T>(schema: z.ZodType<T>, primaryKey: keyof T & string): RxJsonSchema<T> {
    const generated = z.toJSONSchema(schema) as Record<string, unknown>;

    delete generated['$schema'];

    return {
        ...generated,
        primaryKey,
        version: STABLE_ENVELOPE_SCHEMA_VERSION,
    } as RxJsonSchema<T>;
}

interface StableEnvelopeSchemaOptions<T> {
    indexes?: (string | string[])[];
    maxPrimaryKeyLength: number;
    primaryKey: keyof T & string;
    properties?: Record<string, Record<string, unknown>>;
    required?: string[];
    version?: number;
}

function stableEnvelopeSchema<T>({
    indexes,
    maxPrimaryKeyLength,
    primaryKey,
    properties = {},
    required = [],
    version = STABLE_ENVELOPE_SCHEMA_VERSION,
}: StableEnvelopeSchemaOptions<T>): RxJsonSchema<T> {
    return {
        additionalProperties: true,
        ...(indexes ? { indexes } : {}),
        primaryKey,
        properties: {
            [primaryKey]: {
                maxLength: maxPrimaryKeyLength,
                type: 'string',
            },
            ...properties,
        },
        required: [primaryKey, ...required],
        type: 'object',
        version,
    } as unknown as RxJsonSchema<T>;
}
